/* eslint-disable max-len */
import React, { useEffect, useMemo } from "react";
import { PersistGate } from "redux-persist/integration/react";
import { Provider } from "react-redux";
import PropTypes from "prop-types";
import TdFlowWrapper from "./components/td-flow-wrapper";
import defaultTheme from "./theme/default";
import { createStore, deleteStore } from "./redux/store";
import themePropTypes from "./fields/field-prop-types";
import { constants } from "./resource/constants";
import { getMessages } from "./utils/message-service";
import {
  getConsultations,
  cancelConsultation
} from "./utils/consultation-service";
import { createMobileApiRequest } from "./api/mobile-api";
import { logout } from "./api/login";
import fetchMemberNavigation from "./api/member-navigation-fetcher";
import fetchChronicCareToken from "./api/chronic-care-token-fetcher";
import chronicCareTokenLogout from "./api/chronic-care-token-logout";

// To print the version in td-logger
import packageJson from "../package.json";

import "./tailwind.css";

window.location.hash = "";

const { paths, facilityTypes, providerSpecialties } = constants;

export {
  themePropTypes,
  paths,
  defaultTheme,
  getMessages,
  getConsultations,
  cancelConsultation,
  logout,
  facilityTypes,
  providerSpecialties,
  createMobileApiRequest,
  fetchMemberNavigation,
  fetchChronicCareToken,
  chronicCareTokenLogout
};

export * from "@teladoc/inbox";

/**
 * Entrypoint of WebSDK. Wraps \<TdFlowWrapper/> with \<Provider/> to make the [Redux store]
 * {@link https://react-redux.js.org/api/provider} available downstream.
 * <br/>
 * Accepts props and passes it to [TdFlowWrapper]{@link TdFlowWrapper}
 * @param {object} props Configurations passed down from client application
 * @param {string} props.path The name of a screen endpoint
 * @param {object} props.authorization Login credentials (ex. username, password, ssoToken)
 * @param {object} props.appConfiguration Contains configurations for app setup
 * @param {string} [props.appConfiguration.initialState] Configuration object for the requested flow from the client,
 * setting some necessary state for the requested flow
 * @param {string} [props.appConfiguration.googleApiKey] Google API key for Google Maps pharmacy search
 * @param {string} props.appConfiguration.baseUrl Base URL of Mobile API endpoint;
 * Must include trailing "/" at end of URL string
 * @param {string} props.appConfiguration.apiToken  API token for Mobile API endpoint TODO: Get context from MAPI Dev
 * @param {string} props.appConfiguration.bundleIdentifier App version (ex. 'com.td-core')
 * @param {string} [props.appConfiguration.fieldConfigurations={validators}] Configuration object for
 * field validators
 * @param {string} [props.appConfiguration.authToken] Authorization token for the current session
 * @param {string} [props.appConfiguration.authorizedMemberId=null] Passed from client as the current logged in
 * member id, if not present will need to re-authenticate.
 * @param {string} [props.appConfiguration.locale="en"] Configures the language of the app, defaults to English
 * @param {bool} [props.appConfiguration.showBackButton=true] Configures whether or not the back button is shown
 * @param {bool} [props.appConfiguration.browserNavigationEnabled=true] If true, browser back button navigates
 * to previous screen in WebSDK flow; if false browser back button will navigate to page before entering WebSDK
 * @param {string} [props.appConfiguration.piwikAuthToken="84c3a32e4b82f12ff5e751ecea8e13ad"] Piwik authentication token
 * @param {string} [props.appConfiguration.paypalClientId] Paypal credentials for user payment
 * @param {string} [props.appConfiguration.paypalMerchantId] Paypal credentials for user payment
 * @param {string} [props.appConfiguration.idSite="87"] Piwik Analytics identifier for monitoring
 * @returns [TdFlowWrapper]{@link TdFlowWrapper} wrapped in \<Provider/>
 * @see [TdFlowWrapper]{@link TdFlowWrapper}
 */
export function WebSDK(props) {
  const isDfd = props.appConfiguration.properLink;
  const reduxStore = useMemo(() => createStore(isDfd), []);
  const { store, persistor } = reduxStore;

  useEffect(
    () => () => {
      deleteStore(reduxStore);
    },
    []
  );

  // Manually check props for loggingEnabled since the ENV store hasn't loaded yet.
  if (props?.appConfiguration?.loggingEnabled) {
    // eslint-disable-next-line no-console
    console.log("TDLog: WebSDK appConfiguration", {
      ...props.appConfiguration,
      version: packageJson.version
    });
  }

  return (
    <Provider store={store}>
      {persistor ? (
        <PersistGate loading={null} persistor={persistor}>
          <TdFlowWrapper {...props} />
        </PersistGate>
      ) : (
        <TdFlowWrapper {...props} />
      )}
    </Provider>
  );
}

WebSDK.propTypes = {
  appConfiguration: PropTypes.objectOf(Object)
};
